import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, Check, Trash2, Loader2, Upload, ImageOff } from "lucide-react";
import { Toaster, toast } from "sonner";
import { resolveImg } from "@/admin/SiteContent";
import { formatApiErrorDetail } from "@/admin/AuthContext";
import ImageUpload from "@/admin/ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [tempUpload, setTempUpload] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/files`, { withCredentials: true });
      setFiles(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // When user uploads via the ImageUpload widget, refresh
  useEffect(() => {
    if (tempUpload) {
      setTempUpload("");
      load();
    }
  }, [tempUpload]);

  const onCopy = (url) => {
    const full =
      url.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(full);
    setCopied(url);
    toast.success("URL copiato");
    setTimeout(() => setCopied(null), 1600);
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    try {
      await axios.delete(`${API}/files/${id}`, { withCredentials: true });
      toast.success("File rimosso");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="media-library">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "rgba(16,16,22,0.95)",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#F5F5F5",
          },
        }}
      />

      <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
        Cassaforte file
      </p>
      <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide mt-2">
        Media Library
      </h1>
      <p className="mt-2 text-sm text-[#B6B6BE] max-w-2xl">
        Tutte le immagini caricate. Clicca per copiare l&apos;URL e riutilizzarla in
        qualsiasi sezione.
      </p>

      {/* Upload */}
      <div
        className="mt-7 rounded-3xl p-6"
        style={{
          background:
            "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))",
          border: "1px solid rgba(212,175,55,0.18)",
        }}
      >
        <ImageUpload
          value={tempUpload}
          onChange={setTempUpload}
          label="Carica nuovo file"
          height={140}
        />
      </div>

      {/* Grid */}
      <div className="mt-7">
        {loading ? (
          <div className="text-[#B6B6BE] text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Caricamento…
          </div>
        ) : files.length === 0 ? (
          <div className="rounded-3xl p-12 text-center text-[#B6B6BE]" style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
            <ImageOff size={24} className="mx-auto mb-3 text-[#D4AF37]/60" />
            <p className="text-sm">Nessuna immagine caricata.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {files.map((f) => (
              <div
                key={f.id}
                className="rounded-2xl overflow-hidden group relative"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(22,22,29,0.65), rgba(11,11,15,0.92))",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div
                  className="aspect-square w-full overflow-hidden bg-[#0B0B0F]"
                  style={{ background: "#0B0B0F" }}
                >
                  <img
                    src={resolveImg(f.url)}
                    alt={f.original_filename}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-3">
                  <p className="text-xs text-[#F5F5F5] truncate font-semibold">
                    {f.original_filename}
                  </p>
                  <p className="text-[10px] text-[#B6B6BE] uppercase tracking-[0.2em] mt-0.5">
                    {(f.size / 1024).toFixed(0)} KB
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => onCopy(f.url)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      {copied === f.url ? <Check size={11} /> : <Copy size={11} />}
                      {copied === f.url ? "Copiato" : "Copia URL"}
                    </button>
                    <button
                      onClick={() => onDelete(f.id, f.original_filename)}
                      className="inline-flex items-center justify-center w-8 h-7 rounded-full border border-[#E26B6B]/35 text-[#E26B6B] hover:bg-[#E26B6B]/10 transition-colors"
                      aria-label="Elimina"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
